/**
 * PortalSidebar — Linke Navigations-Spalte (Welle P3, Joerg 2026-07-06).
 *
 * Seit 2026-09-17 (Joerg: „unsere Sidebar wird langsam unuebersichtlich"):
 * die Eintraege sind in fuenf Arbeitsbereich-GRUPPEN gegliedert (Mein Bereich,
 * Baustelle, Buero, Wissen & Dokumente, Verwaltung — lib/sidebar-groups.ts)
 * plus dem persoenlichen Mehr-Menue als letzte Pseudo-Gruppe. Gruppen sind
 * standardmaessig ZU (auch die mit der aktiven Seite, die wird nur am Kopf
 * hervorgehoben) und klappen per Hover auf; sie bleiben offen, solange der
 * Zeiger irgendwo in der Sidebar ist (auch auf dem Scrollbalken), und schliessen
 * erst beim Verlassen der Sidebar. Klick auf den Kopf pinnt (LocalStorage).
 * Schmale Sidebar: ein Icon je Gruppe, Hover/Tipp oeffnet ein Flyout rechts.
 * Mobile (<1024px) mountet diese Sidebar gar nicht (PortalShell) — unveraendert.
 *
 * Sichtbarkeit gesteuert ueber `user.sidebar_show` (User-Preference, faellt
 * auf `tenant_settings.sidebar_show_default` zurueck). Kollapszustand
 * persoenlich (`user.sidebar_collapsed`) mit LocalStorage-Cache fuer sofortige
 * Anwendung beim Boot.
 *
 * Nav-Items werden aus `/api/portal/my-access` gefiltert — nur was der User
 * laut RBAC sehen darf. Gruppe je Eintrag: Tenant-Override (Sidebar-Editor,
 * portal_sidebar_label_overrides.section) > Code-Default (NAV_ITEMS.group) bzw.
 * nav_section-Mapping fuer dynamische Module.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Clock, Hammer, Mic, MessageSquare, Library, Truck, LayoutGrid, Search,
  BarChart3, IdCard, ClipboardList, Text, Users as UsersIcon, Calendar,
  CheckSquare, Wrench, Sparkles, ChevronLeft, ChevronRight, CheckCircle2,
  BookOpen, FileText, Headphones, Settings2,
  GripVertical, Eye, EyeOff, Check, MoreHorizontal, CornerLeftUp,
  // RF-4 (2026-08-17) — Icons fuer dynamische DB-Sidebar-Items.
  // Kleine Whitelist statt Star-Import (feedback_lucide_no_star_imports).
  Receipt, Package, Box, Ruler, PieChart, Briefcase, Building, Cog,
  Lightbulb,
  BookOpenCheck,
} from 'lucide-react';
import {
  DndContext, PointerSensor, closestCorners, useDroppable, useSensor, useSensors,
  type DragEndEvent, type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api, useAuth } from '@onvice/portal-shell-api';

import { useSidebarBadges, type BadgeSnapshot } from '../lib/sidebar-badge-stream';
import { useFederatedStatus } from '../lib/useFederatedStatus';
import type { ModuleStatus } from '../modules/registry';
import {
  SIDEBAR_GROUPS, MEHR_GROUP, isSidebarGroupSlug, mapNavSectionToGroup, resolveGroup,
  type SidebarGroupKey, type SidebarGroupSlug, type SidebarOverride,
} from '../lib/sidebar-groups';
import SidebarGroup, { isFinePointer } from './sidebar/SidebarGroup';

/**
 * Federation-Status-Punkt neben dem Icon.
 * NUR rot bei down — 'up' und 'unknown' rendern nichts (Joerg 2026-08-03:
 * gruene Punkte ueberladen die Sidebar, wir wollen nur Alarme sehen).
 * Reagiert live auf federation-status-change-Event ohne Reload.
 */
function StatusDot({ slug }: { slug?: string }) {
  const status: ModuleStatus = useFederatedStatus(slug ?? null);
  if (!slug || status !== 'down') return null;
  return (
    <span
      className="absolute -top-0.5 -left-0.5 w-2 h-2 rounded-full ring-1 ring-slate-900 bg-rose-500"
      title={`${slug}: Modul offline`}
      aria-label={`${slug}: Modul offline`}
    />
  );
}

/**
 * Portalweite Suche (PS-4, 2026-09-09): Suchfeld oben in der Sidebar.
 * Enter oder Lupen-Klick fuehrt zur kategorisierten Ergebnis-Ansicht /suche.
 * Eingeklappte Sidebar zeigt nur den Lupen-Button.
 */
function SidebarSearchField({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const submit = () => {
    const q = term.trim();
    navigate(q.length >= 2 ? `/suche?q=${encodeURIComponent(q)}` : '/suche');
  };
  if (collapsed) {
    return (
      <div className="px-2 py-2 border-b border-default flex justify-center">
        <button
          type="button"
          onClick={() => navigate('/suche')}
          className="p-2 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary"
          title="Portalweite Suche"
          aria-label="Portalweite Suche"
        >
          <Search size={18} />
        </button>
      </div>
    );
  }
  return (
    <form
      className="px-3 py-2 border-b border-default"
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      role="search"
    >
      <div className="relative">
        <input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Suchen …"
          aria-label="Portalweite Suche"
          className="w-full rounded-md border border-default bg-background pl-3 pr-9 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <button
          type="submit"
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary"
          title="Suchen"
          aria-label="Suchen"
        >
          <Search size={16} />
        </button>
      </div>
    </form>
  );
}

export interface NavItem {
  slug: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  href: string;
  /** Arbeitsbereich-Gruppe (Code-Default; Tenant-Override via Sidebar-Editor). */
  group: SidebarGroupSlug;
  iconClassName?: string; // für Unternehmensglossar-Accent
  requiresModule?: string; // Modul-Slug aus my-access
  badgeKey?: 'todos' | 'chat' | 'baustelle' | 'freigaben' | 'tagebuch';
  // 2026-07-07 (Joerg): Persona-Filter — wenn gesetzt, Item nur ausblenden
  // wenn die aktive Persona in dieser Liste steht (Handwerker sieht kein Adressbuch).
  hideForPersonas?: string[];
  // 2026-07-08 (Joerg): Zusaetzlicher Permission-Guard — Item nur zeigen, wenn
  // der User diese Permission (oder Admin) hat. Fuer Sonderfaelle wie
  // Unternehmensglossar (bauberichteV2-Reader darf lesen, aber Sidebar-Link
  // nur wenn glossary.read tatsaechlich vorhanden ist — gleiche Semantik wie
  // die Route selbst).
  requiresPermission?: string;
  /** 2026-09-30 (Rapportzettel): Eintrag nur, wenn eine Modul-Einstellung an ist —
   *  `GET /api/modules/<module><path>` → `data[key]` wahr. Bis zur Antwort verborgen. */
  requiresFeature?: { module: string; path: string; key: string };
}

// Reihenfolge = Default-Reihenfolge innerhalb der Gruppen (Joerg-Zuordnung 2026-09-17).
// Wird auch vom Admin-UI genutzt (Gruppen-Blacklist-Editor, Sidebar-Editor). SP1 2026-07-21.
export const NAV_ITEMS: NavItem[] = [
  // ---- Mein Bereich ----
  { slug: 'home',          label: 'Startseite',         icon: Home,           href: '/',                group: 'mein-bereich' },
  { slug: 'zeiterfassung', label: 'Zeiterfassung',      icon: Clock,          href: '/m/zeiterfassung', group: 'mein-bereich', requiresModule: 'zeiterfassung' },
  { slug: 'todos',         label: 'Meine Todos',        icon: CheckSquare,    href: '/todos',           group: 'mein-bereich', badgeKey: 'todos' },
  { slug: 'kalender',      label: 'Kalender',           icon: Calendar,       href: '/kalender',        group: 'mein-bereich' },
  { slug: 'onvi-capture',  label: 'Notiz',              icon: Sparkles,       href: '/m/onvi-capture',  group: 'mein-bereich', requiresModule: 'onvi-capture' },
  { slug: 'chat',          label: 'Chat',               icon: MessageSquare,  href: '/m/chat',          group: 'mein-bereich', requiresModule: 'chat', badgeKey: 'chat' },
  { slug: 'glossar',       label: 'Mein Glossar',       icon: Text,           href: '/m/bauberichteV2/glossar',       group: 'mein-bereich', requiresModule: 'bauberichteV2', requiresPermission: 'glossary.read' },
  // ---- Baustelle ----
  // Jörg 2026-10-08: Eintrag heißt „Heute“ (öffnet die Heute-Seite), Gruppe bleibt „Baustelle“.
  { slug: 'bauberichteV2', label: 'Heute',              icon: BookOpenCheck,  href: '/m/bauberichteV2', group: 'baustelle', requiresModule: 'bauberichteV2', badgeKey: 'baustelle' },
  // Sub-Links unter Baustelle (2026-07-14): direkt auf die haeufigsten
  // BV2-Unterseiten. Berichte + Aufgaben + Tagebuch.
  { slug: 'aufgaben',      label: 'Aufgaben',           icon: CheckSquare,    href: '/m/bauberichteV2/tasks',          group: 'baustelle', requiresModule: 'bauberichteV2' },
  { slug: 'berichte',      label: 'Berichte',           icon: FileText,       href: '/m/bauberichteV2/reports',        group: 'baustelle', requiresModule: 'bauberichteV2' },
  // 2026-09-30 (Jörg): Rapportzettel als eigene, durchsuchbare Ansicht neben Berichten und
  // Aufgaben — nur wenn die Funktion in den Modul-Einstellungen der Baustelle an ist.
  { slug: 'rapporte',      label: 'Rapporte',           icon: ClipboardList,  href: '/m/bauberichteV2/rapporte',       group: 'baustelle', requiresModule: 'bauberichteV2', requiresFeature: { module: 'bauberichteV2', path: '/rapport-rules', key: 'enabled' } },
  // Tagebuch (2026-07-14, Joerg): persoenliche Notizen unter BV2, aber eigener
  // Sidebar-Link — nicht im BV2-Modul-Kopfmenue.
  { slug: 'tagebuch',      label: 'Tagebuch',           icon: BookOpen,       href: '/m/bauberichteV2/journal',        group: 'baustelle', requiresModule: 'bauberichteV2', badgeKey: 'tagebuch' },
  { slug: 'plantafel',     label: 'Plantafel',          icon: LayoutGrid,     href: '/m/plantafel',     group: 'baustelle', requiresModule: 'plantafel' },
  { slug: 'ressourcen',    label: 'Ressourcen',         icon: Wrench,         href: '/m/ressourcen',    group: 'baustelle', requiresModule: 'ressourcen' },
  { slug: 'fuhrpark',      label: 'Fuhrpark',           icon: Truck,          href: '/m/fuhrpark',      group: 'baustelle', requiresModule: 'fuhrpark' },
  // ---- Buero ----
  // 2026-07-07 (Joerg): Genehmigungs-Sammelmappe fuer die GF — nur sichtbar
  // wenn zeiterfassung.approve vorhanden (siehe visibleItems-Filter unten).
  { slug: 'genehmigungen', label: 'Genehmigungen',      icon: CheckCircle2,   href: '/genehmigungen',   group: 'buero', badgeKey: 'freigaben' },
  // 2026-07-21 (Joerg): Service-Desk fehlte trotz seit 2026-07-03 aktivem
  // Container-Bestand komplett in NAV_ITEMS. ACL-Seed war da (seed-module-access
  // Zeile 46/63), aber der Slug wurde nie in die Sidebar-Liste eingetragen.
  { slug: 'service-desk',  label: 'Service-Desk',       icon: Headphones,     href: '/m/service-desk',  group: 'buero', requiresModule: 'service-desk' },
  { slug: 'adressbuch',    label: 'Adressbuch',         icon: UsersIcon,      href: '/admin/adressbuch',group: 'buero', requiresModule: 'adressbuch' },
  { slug: 'stammdaten',    label: 'Stammdaten',         icon: IdCard,         href: '/m/stammdaten',    group: 'buero', requiresModule: 'stammdaten' },
  // 2026-09-17: Modul heisst `disposition` (Route /m/disposition) — der alte
  // Slug `auswertungen` als requiresModule existierte nirgends, der Eintrag
  // war seit Monaten unsichtbar. Sidebar-Slug bleibt `auswertungen` (Prefs/
  // Blacklists sind darauf gekeyt).
  { slug: 'auswertungen',  label: 'Auswertungen',       icon: BarChart3,      href: '/m/disposition',   group: 'buero', requiresModule: 'disposition' },
  // ---- Wissen & Dokumente ----
  { slug: 'bibliothek',    label: 'Bibliothek',         icon: Library,        href: '/m/bibliothek',    group: 'wissen-dokumente', requiresModule: 'bibliothek' },
  // 2026-07-20 (DMS-D2d): Haupt-Modul-Eintrag fuer alle die dms.read haben.
  // Label 2026-09-17 „DMS" → „Dokumente" (kein Entwickler-Jargon; Tenant-Alias gewinnt).
  { slug: 'dms',           label: 'Dokumente',          icon: FileText,       href: '/m/dms',                         group: 'wissen-dokumente', requiresModule: 'dms', requiresPermission: 'dms.read' },
  { slug: 'vorlagen',      label: 'Vorlagen',           icon: ClipboardList,  href: '/m/vorlagen',      group: 'wissen-dokumente', requiresModule: 'vorlagen' },
  { slug: 'firmen-glossar',label: 'Unternehmensglossar',icon: Text,           href: '/m/bauberichteV2/admin/glossar', group: 'wissen-dokumente', requiresModule: 'bauberichteV2', requiresPermission: 'glossary.read', iconClassName: 'text-accent' },
  // ---- Verwaltung ----
  // 2026-07-20 (DMS-D1): Verwaltungs-Link zum Doc-Type-Katalog — nur sichtbar
  // wenn User dms.configure hat (Admin greift ohnehin ueber).
  { slug: 'dms-typen',     label: 'Dokumenttypen',      icon: FileText,       href: '/admin/dms-typen',               group: 'verwaltung', requiresModule: 'dms', requiresPermission: 'dms.configure', iconClassName: 'text-accent' },
  // 2026-07-07 (Joerg): Assistent aus der Sidebar entfernt — das blaue Avatar-
  // Badge oben rechts ist bereits Dauer-Zugang. Mobile-Pulldown enthaelt ihn
  // ebenfalls (MobileHomeTabBar MORE_ITEMS).
];

const LS_COLLAPSED = 'onvice.sidebar.collapsed';
const LS_PINNED = 'onvice.sidebar.groups.pinned';
/** dnd-kit-ID der Ablageflaeche einer Gruppe (`group:<slug>`) — Ziel beim
 *  Ziehen in eine leere Gruppe (2026-09-18). */
const DROP_PREFIX = 'group:';
const AUTO_COLLAPSE_PATHS = ['/m/plantafel', '/m/disposition'];
const HOVER_LEAVE_MS = 150;
// Joerg 2026-09-17: „leicht verzoegert aufswipen" — Hover oeffnet erst nach
// kurzer Verweildauer (kein Aufflackern beim Durchfahren mit der Maus).
const HOVER_OPEN_MS = 250; // Joerg 17.09. abends: kam mit 120 ms noch zu ploetzlich

// RF-4 (2026-08-17) — Icon-Whitelist fuer dynamische DB-Sidebar-Items.
// Modul-Manifest liefert `nav.icon` als String (z.B. "Receipt"), wir mappen
// auf die Lucide-Component. Fallback ist `Box`. Extensible ohne Portal-Rebuild:
// wenn ein neues Modul ein Icon nutzt das hier fehlt, sieht der User Box.
// Whitelist statt Star-Import wegen feedback_lucide_no_star_imports.
const DYNAMIC_NAV_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Home, Clock, Hammer, MessageSquare, Library, Truck, LayoutGrid, BarChart3,
  IdCard, ClipboardList, Text, Users: UsersIcon, Calendar, CheckSquare,
  Wrench, Sparkles, CheckCircle2, BookOpen, FileText, Headphones, Mic,
  Receipt, Package, Box, Ruler, PieChart, Briefcase, Building, Cog,
  Lightbulb,
};

interface AvailableModuleResponse {
  slug: string;
  name: string;
  alias: string | null;
  label: string;
  icon: string;
  version: string;
  remoteEntry: string;
  nav: {
    section: string;
    label: string;
    route: string;
    icon: string;
    badgeEndpoint: string | null;
  } | null;
}

/**
 * RF-4 (2026-08-17) — Dynamische Sidebar-Items aus /api/portal/modules/available.
 *
 * Bestehende Module bleiben unangetastet (kommen aus statischer NAV_ITEMS-
 * Konstante). Neue Module (z.B. Faktura) registrieren ihre Nav-Info via
 * registerSelf() in portal_module_registry und tauchen dann hier auf — OHNE
 * dass NAV_ITEMS erweitert werden muss oder das Portal neu gebaut wird.
 * Gruppe: `nav_section` → mapNavSectionToGroup (2026-09-17).
 *
 * Dedup: Slugs die schon in NAV_ITEMS stehen werden hier ignoriert (sonst
 * Doppel-Anzeige waehrend der Uebergangsphase). NAV_ITEMS gewinnt.
 */
function useDynamicSidebarModules(visible: boolean): NavItem[] {
  const [items, setItems] = useState<NavItem[]>([]);
  useEffect(() => {
    if (!visible) return;
    api.get<{ success: boolean; data: AvailableModuleResponse[] }>('/portal/modules/available')
      .then((res) => {
        const staticSlugs = new Set(NAV_ITEMS.map((it) => it.slug));
        const dyn: NavItem[] = (res.data ?? [])
          .filter((m) => m.nav !== null && !staticSlugs.has(m.slug))
          .map((m): NavItem => ({
            slug: m.slug,
            label: m.nav!.label,
            icon: DYNAMIC_NAV_ICONS[m.nav!.icon] ?? Box,
            href: m.nav!.route,
            group: mapNavSectionToGroup(m.nav!.section, m.slug),
            requiresModule: m.slug,
          }));
        setItems(dyn);
      })
      .catch(() => setItems([]));
  }, [visible]);
  return items;
}

interface AccessResponse {
  departments: Array<{ modules: Array<{ slug: string }> }>;
}

interface PrefsResponse {
  sidebar_show: boolean | null;
  sidebar_collapsed: boolean;
  home_layout: 'picker' | 'tabs' | 'dashboard';
  sidebar_show_default: boolean;
}

interface Props {
  visible: boolean;
  onVisibilityChange?: (visible: boolean) => void;
}

function isActiveHref(location: string, href: string): boolean {
  return location === href || (href !== '/' && location.startsWith(href));
}

function readPinned(): Set<SidebarGroupKey> {
  try {
    const raw = localStorage.getItem(LS_PINNED);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? (arr.filter((x) => typeof x === 'string') as SidebarGroupKey[]) : []);
  } catch {
    return new Set();
  }
}

export default function PortalSidebar({ visible }: Props) {
  const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem(LS_COLLAPSED) === '1');
  const [allowedModules, setAllowedModules] = useState<Set<string>>(new Set());
  const location = useLocation();
  const badges = useSidebarBadges();
  const { hasPermission, isAdmin, persona, user } = useAuth();

  // SP1 2026-07-21: Gruppen-Blacklist + persoenliche Personalisierung.
  // - blacklistSlugs: Union der Blacklists aller Adressbuch-Gruppen des Users
  //   (leer wenn Admin/GF durch Wildcard bypassed).
  // - userOrder: persoenliche Reihenfolge (null = Default), moreSlugs = im
  //   Mehr-Menue statt Haupt-Sidebar, hiddenSlugs = ganz versteckt.
  // - editMode: sichtbar nur wenn User in den Bearbeiten-Modus wechselt.
  const [blacklistSlugs, setBlacklistSlugs] = useState<Set<string>>(new Set());
  const [userOrder, setUserOrder] = useState<string[] | null>(null);
  const [moreSlugs, setMoreSlugs] = useState<Set<string>>(new Set());
  const [hiddenSlugs, setHiddenSlugs] = useState<Set<string>>(new Set());
  const [editMode, setEditMode] = useState(false);
  // 2026-09-18 (Joerg): persoenliche Gruppen-Zuordnung je Slug — Eintraege
  // per Ziehen in eine andere Gruppe. Liegt UEBER Tenant-Override + Default,
  // wird mit „Alle Anpassungen zuruecksetzen" geleert.
  const [groupSlugs, setGroupSlugs] = useState<Record<string, SidebarGroupSlug>>({});
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tiefenlink fuer Tests/Handbuch: ?sidebarEdit=1 oeffnet den Bearbeiten-Modus.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('sidebarEdit') === '1') setEditMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gruppen-Zustand (2026-09-17): gepinnt (LocalStorage), Hover (breit),
  // Flyout (schmal). Hover/Flyout schliessen erst beim Verlassen der ganzen
  // Sidebar (150 ms), nie beim Scrollen.
  const [pinned, setPinned] = useState<Set<SidebarGroupKey>>(readPinned);
  const [hoverGroup, setHoverGroup] = useState<SidebarGroupKey | null>(null);
  const [flyoutGroup, setFlyoutGroup] = useState<SidebarGroupKey | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    try { localStorage.setItem(LS_PINNED, JSON.stringify(Array.from(pinned))); } catch { /* egal */ }
  }, [pinned]);

  // Auto-Kollaps auf Whitelist-Routes
  const [manualCollapse, setManualCollapse] = useState<boolean | null>(null);
  const shouldAutoCollapse = useMemo(() => {
    return AUTO_COLLAPSE_PATHS.some((p) => location.pathname.startsWith(p));
  }, [location.pathname]);
  useEffect(() => {
    if (shouldAutoCollapse && manualCollapse === null) {
      setManualCollapse(collapsed);
      setCollapsed(true);
    } else if (!shouldAutoCollapse && manualCollapse !== null) {
      setCollapsed(manualCollapse);
      setManualCollapse(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoCollapse]);

  // Routenwechsel: Hover/Flyout zu (ein Klick auf einen Eintrag navigiert).
  useEffect(() => {
    setHoverGroup(null);
    setFlyoutGroup(null);
  }, [location.pathname]);

  // Sidebar-Overrides (2026-08-17) — tenant-spezifischer Alias + Sichtbarkeit
  // pro Slug, seit 2026-09-17 auch die Gruppe (`section`). Wird von Admin ueber
  // /admin/adressbuch?tab=sidebar gepflegt. Renderer wendet Overrides als
  // LETZTEN Schritt im visibleItems/dynamicFiltered-Pipeline an.
  const [sidebarOverrides, setSidebarOverrides] = useState<Record<string, SidebarOverride>>({});
  useEffect(() => {
    if (!visible) return;
    api.get<{ success: boolean; data: Record<string, Partial<SidebarOverride>> }>('/portal/sidebar-overrides')
      .then((res) => {
        const map: Record<string, SidebarOverride> = {};
        for (const [slug, ov] of Object.entries(res.data ?? {})) {
          map[slug] = { alias: ov.alias ?? null, hidden: !!ov.hidden, section: ov.section ?? null };
        }
        setSidebarOverrides(map);
      })
      .catch(() => setSidebarOverrides({}));
  }, [visible]);

  // my-access laden für RBAC-Filter
  useEffect(() => {
    if (!visible) return;
    api.get<{ success: boolean; data: AccessResponse }>('/portal/my-access')
      .then((res) => {
        const slugs = new Set<string>();
        for (const dept of res.data?.departments ?? []) {
          for (const mod of dept.modules ?? []) slugs.add(mod.slug);
        }
        setAllowedModules(slugs);
      })
      .catch(() => setAllowedModules(new Set()));
  }, [visible]);

  // 2026-09-30: Feature-Schalter je Modul (z. B. Rapportzettel) — nur für erreichbare Module laden.
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!visible) return;
    const wanted = NAV_ITEMS.filter((it) => it.requiresFeature && allowedModules.has(it.requiresFeature.module));
    if (wanted.length === 0) return;
    let cancelled = false;
    for (const it of wanted) {
      const f = it.requiresFeature!;
      const id = `${f.module}${f.path}#${f.key}`;
      api.get<{ success?: boolean; data?: Record<string, unknown> }>(`/modules/${f.module}${f.path}`)
        .then((res) => { if (!cancelled) setFeatures((prev) => ({ ...prev, [id]: !!res?.data?.[f.key] })); })
        .catch(() => { if (!cancelled) setFeatures((prev) => ({ ...prev, [id]: false })); });
    }
    return () => { cancelled = true; };
  }, [visible, allowedModules]);

  // SP1: Sidebar-Prefs + Gruppen-Blacklist laden
  useEffect(() => {
    if (!visible) return;
    api.get<{ success: boolean; data: {
      order: string[] | null;
      moreSlugs: string[];
      hiddenSlugs: string[];
      groupSlugs: Record<string, string> | null;
      blacklist: string[];
      isWildcardBypass: boolean;
    } }>('/portal/me/sidebar-prefs')
      .then((res) => {
        const d = res.data;
        setUserOrder(d.order ?? null);
        setMoreSlugs(new Set(d.moreSlugs ?? []));
        setHiddenSlugs(new Set(d.hiddenSlugs ?? []));
        const gs: Record<string, SidebarGroupSlug> = {};
        for (const [slug, g] of Object.entries(d.groupSlugs ?? {})) {
          if (isSidebarGroupSlug(g)) gs[slug] = g;
        }
        setGroupSlugs(gs);
        setBlacklistSlugs(new Set(d.blacklist ?? []));
      })
      .catch(() => { /* Defaults bleiben (leer) */ });
  }, [visible]);

  // Debounced Persist der User-Prefs (order/more/hidden).
  // SD-2026-00033 (Onvice, 2026-07-31): Vorher `.catch(() => {})` — jeder
  // Server-Fehler wurde still verschluckt, Nutzer sah nichts, Reihenfolge
  // ging beim Refresh verloren. Jetzt Console-Warn plus Log der versendeten
  // Payload — bei Prod-Meldung koennen Debug-Logs die Ursache eingrenzen.
  const persistPrefs = useCallback((patch: {
    sidebar_order?: string[] | null;
    sidebar_more_slugs?: string[];
    sidebar_hidden_slugs?: string[];
    sidebar_group_slugs?: Record<string, string> | null;
  }) => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      void api.patch('/portal/me/sidebar-prefs', patch).catch((err: unknown) => {
        console.warn('[Sidebar] persistPrefs fehlgeschlagen:', (err as Error)?.message, 'patch:', patch);
      });
    }, 500);
  }, []);

  function toggleCollapse() {
    const next = !collapsed;
    setCollapsed(next);
    setManualCollapse(null);
    clearOpen();
    setHoverGroup(null);
    setFlyoutGroup(null);
    localStorage.setItem(LS_COLLAPSED, next ? '1' : '0');
    // Server-Persist ohne Rückwarten
    void api.patch('/portal/me/sidebar-prefs', { sidebar_collapsed: next }).catch((err: unknown) => {
      console.warn('[Sidebar] sidebar_collapsed persist fehlgeschlagen:', (err as Error)?.message);
    });
  }

  const visibleItems = useMemo(() => {
    // 2026-07-07 (Joerg): GF-User landen beim Klick auf "Zeiterfassung" direkt
    // auf der Team-Monat/Zeitkonten-Seite, nicht auf ihrer eigenen StampPage.
    // Andere User (Handwerker/Bueero ohne approve) bleiben auf StampPage.
    // Zusaetzlich: Genehmigungs-Sammelmappe nur fuer User mit approve-Recht.
    const zeMayApprove = isAdmin || hasPermission('zeiterfassung.approve');
    // Effektive Persona: aktiver Persona-Switcher hat Vorrang, sonst faellt der
    // Filter auf die echte User-Rolle zurueck (Bob als echter Handwerker soll
    // das Adressbuch NICHT in der Sidebar sehen — vorher griff hideForPersonas
    // nur beim Persona-Switch, Joerg 2026-07-10).
    const effectivePersona = persona.active ? persona.personaKey : user?.role;
    return NAV_ITEMS
      .filter((it) => !it.requiresModule || allowedModules.has(it.requiresModule))
      // SP1: Gruppen-Blacklist blockt auch wenn ACL erlauben wuerde.
      // Wildcard-Bypass (Admin/GF) hat blacklistSlugs=[] vom Backend geliefert.
      .filter((it) => !blacklistSlugs.has(it.slug))
      // Joerg 2026-09-11: Genehmigungen richtet sich nach der SEITEN-Sperre
      // (Gruppe Geschaeftsfuehrung/Admin) — der Snapshot liefert das Flag vom
      // Backend. Solange kein Snapshot da ist, gilt die alte approve-Pruefung
      // (kein Flackern beim Laden; das Backend zaehlt ohnehin fail-closed 0).
      .filter((it) => it.slug !== 'genehmigungen' || (badges.genehmigungenSichtbar ?? zeMayApprove))
      .filter((it) => !it.hideForPersonas || !effectivePersona || !it.hideForPersonas.includes(effectivePersona))
      .filter((it) => !it.requiresPermission || isAdmin || hasPermission(it.requiresPermission))
      // 2026-09-30: Feature-Schalter (Modul-Einstellung) — bis zur Antwort verborgen.
      .filter((it) => !it.requiresFeature || !!features[`${it.requiresFeature.module}${it.requiresFeature.path}#${it.requiresFeature.key}`])
      // 2026-08-17: Tenant-Sichtbarkeits-Override (portal_sidebar_label_overrides.hidden).
      .filter((it) => !sidebarOverrides[it.slug]?.hidden)
      .map((it) => {
        // Alias + Gruppe anwenden (Priority: Tenant-Override > Default).
        const ov = sidebarOverrides[it.slug];
        const withOv: NavItem = {
          ...it,
          label: ov?.alias ? ov.alias : it.label,
          // Persoenliche Zuordnung (Ziehen in andere Gruppe) > Tenant-Override > Default
          group: groupSlugs[it.slug] ?? resolveGroup(it.group, ov?.section),
        };
        if (withOv.slug === 'home' && zeMayApprove) {
          // Rollenspezifische Startseite: GF/Admin bekommen die feste
          // Heute-Live-Sicht (/home/gf) statt der Modul-Picker-Startseite.
          return { ...withOv, href: '/home/gf' };
        }
        if (withOv.slug === 'zeiterfassung' && zeMayApprove) {
          return { ...withOv, href: '/m/zeiterfassung/team-monat' };
        }
        return withOv;
      });
  }, [allowedModules, blacklistSlugs, hasPermission, isAdmin, persona, user, sidebarOverrides, groupSlugs, badges.genehmigungenSichtbar, features]);

  // RF-4 (2026-08-17) — Dynamische Sidebar-Items aus /api/portal/modules/available.
  // Neue Module aus DB werden angehaengt, sobald sie in allowedModules stehen
  // (RBAC-Filter identisch zu statischen Items).
  const dynamicItems = useDynamicSidebarModules(visible);
  const dynamicFiltered = useMemo(
    () => dynamicItems
      .filter((it) => !it.requiresModule || allowedModules.has(it.requiresModule))
      .filter((it) => !blacklistSlugs.has(it.slug))
      // 2026-08-17: gleiche Overrides-Pipeline wie fuer statische Items.
      .filter((it) => !sidebarOverrides[it.slug]?.hidden)
      .map((it): NavItem => {
        const ov = sidebarOverrides[it.slug];
        return { ...it, label: ov?.alias ? ov.alias : it.label, group: groupSlugs[it.slug] ?? resolveGroup(it.group, ov?.section) };
      }),
    [dynamicItems, allowedModules, blacklistSlugs, sidebarOverrides, groupSlugs],
  );

  // SP1 + Gruppen (2026-09-17): EINE flache Reihenfolge ueber ALLE Items
  // (userOrder, Fallback NAV_ITEMS-Reihenfolge + dynamische hinten), darueber
  // liegt zur Laufzeit die Gruppierung. moreSlugs landen im Mehr-Menue,
  // hiddenSlugs erscheinen NUR im Bearbeiten-Modus. Adressbuch/Kalender/Todos
  // nehmen seit 2026-09-17 ebenfalls an der Personalisierung teil (vorher fix).
  const allItems = useMemo(() => [...visibleItems, ...dynamicFiltered], [visibleItems, dynamicFiltered]);
  const orderedAll = useMemo(() => {
    if (!userOrder || userOrder.length === 0) return allItems;
    const idx = new Map(userOrder.map((s, i) => [s, i]));
    return [...allItems].sort((a, b) => {
      const ai = idx.get(a.slug) ?? 1000 + allItems.findIndex((x) => x.slug === a.slug);
      const bi = idx.get(b.slug) ?? 1000 + allItems.findIndex((x) => x.slug === b.slug);
      return ai - bi;
    });
  }, [allItems, userOrder]);
  const mainItems = useMemo(
    () => orderedAll.filter((it) => !moreSlugs.has(it.slug) && !hiddenSlugs.has(it.slug)),
    [orderedAll, moreSlugs, hiddenSlugs],
  );
  const moreItems = useMemo(
    () => orderedAll.filter((it) => moreSlugs.has(it.slug) && !hiddenSlugs.has(it.slug)),
    [orderedAll, moreSlugs, hiddenSlugs],
  );
  const hiddenItems = useMemo(() => orderedAll.filter((it) => hiddenSlugs.has(it.slug)), [orderedAll, hiddenSlugs]);

  // Gruppen mit Inhalt, in fester Reihenfolge — leere Gruppen werden nicht gerendert.
  // Im Bearbeiten-Modus bleiben leere Gruppen als Ablageflaeche stehen (2026-09-18).
  const groups = useMemo(
    () => SIDEBAR_GROUPS
      .map((def) => ({ def, items: mainItems.filter((it) => it.group === def.slug) }))
      .filter((g) => g.items.length > 0 || (editMode && !collapsed)),
    [mainItems, editMode, collapsed],
  );

  // Gruppe der aktiven Route (laengster passender href gewinnt — /m/bauberichteV2
  // matcht auch /m/bauberichteV2/tasks). Nur Hervorhebung, KEIN Auto-Open.
  const activeGroup = useMemo<SidebarGroupKey | null>(() => {
    let best: { len: number; key: SidebarGroupKey } | null = null;
    const consider = (it: NavItem, key: SidebarGroupKey) => {
      if (!isActiveHref(location.pathname, it.href)) return;
      if (!best || it.href.length > best.len) best = { len: it.href.length, key };
    };
    for (const it of mainItems) consider(it, it.group);
    for (const it of moreItems) consider(it, 'mehr');
    return best ? (best as { len: number; key: SidebarGroupKey }).key : null;
  }, [mainItems, moreItems, location.pathname]);

  // Badge-Summe + Tooltip je Gruppe (Summe der Zahlen-Badges der Eintraege).
  const badgeInfo = useCallback((items: NavItem[]) => {
    let sum = 0;
    const tips: string[] = [];
    for (const it of items) {
      if (!it.badgeKey) continue;
      const n = badges[it.badgeKey] ?? 0;
      if (n > 0) {
        sum += n;
        const tip = badges.tooltips?.[it.badgeKey];
        if (tip) tips.push(tip);
      }
    }
    return { sum, title: tips.length > 0 ? tips.join(' · ') : undefined };
  }, [badges]);

  // ---- Hover / Klick / Flyout (Regeln siehe SidebarGroup.tsx-Kopf) ----
  const clearLeave = useCallback(() => {
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
  }, []);
  const clearOpen = useCallback(() => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
  }, []);
  const scheduleLeave = useCallback(() => {
    clearLeave();
    clearOpen();
    leaveTimer.current = setTimeout(() => {
      setHoverGroup(null);
      setFlyoutGroup(null);
    }, HOVER_LEAVE_MS);
  }, [clearLeave, clearOpen]);
  // Hover oeffnet verzoegert (HOVER_OPEN_MS); Wechsel auf einen anderen Kopf
  // startet den Timer neu, Verlassen der Sidebar bricht ihn ab.
  const onHoverStart = useCallback((slug: SidebarGroupKey) => {
    clearLeave();
    clearOpen();
    openTimer.current = setTimeout(() => {
      if (collapsed) setFlyoutGroup(slug);
      else setHoverGroup(slug);
    }, HOVER_OPEN_MS);
  }, [clearLeave, clearOpen, collapsed]);
  const onHeadClick = useCallback((slug: SidebarGroupKey) => {
    if (collapsed) {
      // Schmal: bei Maus regelt der Hover, bei Touch toggelt der Tipp.
      if (!isFinePointer()) setFlyoutGroup((cur) => (cur === slug ? null : slug));
      return;
    }
    // Breit: Klick pinnt/entpinnt (bei Touch faktisch Auf/Zu, da kein Hover).
    setPinned((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  }, [collapsed]);
  const closeFlyout = useCallback(() => setFlyoutGroup(null), []);

  // Actions fuer Bearbeiten-Modus
  const moveToMore = useCallback((slug: string) => {
    setMoreSlugs((prev) => {
      const next = new Set(prev); next.add(slug);
      persistPrefs({ sidebar_more_slugs: Array.from(next) });
      return next;
    });
  }, [persistPrefs]);
  const moveToMain = useCallback((slug: string) => {
    setMoreSlugs((prev) => {
      const next = new Set(prev); next.delete(slug);
      persistPrefs({ sidebar_more_slugs: Array.from(next) });
      return next;
    });
    setHiddenSlugs((prev) => {
      if (!prev.has(slug)) return prev;
      const next = new Set(prev); next.delete(slug);
      persistPrefs({ sidebar_hidden_slugs: Array.from(next) });
      return next;
    });
  }, [persistPrefs]);
  const hideItem = useCallback((slug: string) => {
    setHiddenSlugs((prev) => {
      const next = new Set(prev); next.add(slug);
      persistPrefs({ sidebar_hidden_slugs: Array.from(next) });
      return next;
    });
    setMoreSlugs((prev) => {
      if (!prev.has(slug)) return prev;
      const next = new Set(prev); next.delete(slug);
      persistPrefs({ sidebar_more_slugs: Array.from(next) });
      return next;
    });
  }, [persistPrefs]);
  // Ziehen (2026-09-17 innerhalb, seit 2026-09-18 auch ZWISCHEN Gruppen, Joerg):
  // EIN DndContext ueber allen Gruppen. Waehrend des Ziehens (onDragOver) wandert
  // der Eintrag in die Zielgruppe (persoenliche Zuordnung) und in der flachen
  // Gesamtreihenfolge an die Stelle des Eintrags, ueber dem er schwebt — bzw. ans
  // Ende der Zielgruppe, wenn er auf deren leere Ablageflaeche trifft. onDragEnd
  // sortiert innerhalb der Gruppe fertig (die Positionen der Gruppen-Eintraege in
  // der flachen Reihenfolge werden der Reihe nach neu belegt, andere Gruppen und
  // das Mehr-Menue bleiben stabil) und persistiert Reihenfolge + Zuordnung.
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const groupOfSlug = useCallback(
    (slug: string): SidebarGroupSlug | null => mainItems.find((it) => it.slug === slug)?.group ?? null,
    [mainItems],
  );
  const handleDragOver = useCallback((e: DragOverEvent) => {
    const activeSlug = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || overId === activeSlug) return;
    const from = groupOfSlug(activeSlug);
    const to = overId.startsWith(DROP_PREFIX)
      ? (isSidebarGroupSlug(overId.slice(DROP_PREFIX.length)) ? overId.slice(DROP_PREFIX.length) as SidebarGroupSlug : null)
      : groupOfSlug(overId);
    if (!from || !to || from === to) return;
    setGroupSlugs((prev) => ({ ...prev, [activeSlug]: to }));
    const base = orderedAll.map((it) => it.slug).filter((s) => s !== activeSlug);
    let insertAt = base.length;
    if (!overId.startsWith(DROP_PREFIX)) {
      const idx = base.indexOf(overId);
      if (idx >= 0) insertAt = idx;
    } else {
      const last = [...mainItems].reverse().find((it) => it.group === to && it.slug !== activeSlug);
      if (last) insertAt = base.indexOf(last.slug) + 1;
    }
    base.splice(insertAt, 0, activeSlug);
    setUserOrder(base);
  }, [groupOfSlug, orderedAll, mainItems]);
  const handleDragEnd = useCallback((e: DragEndEvent) => {
    const activeSlug = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    const group = groupOfSlug(activeSlug);
    let order = orderedAll.map((it) => it.slug);
    if (overId && overId !== activeSlug && !overId.startsWith(DROP_PREFIX) && group && groupOfSlug(overId) === group) {
      const items = mainItems.filter((it) => it.group === group).map((it) => it.slug);
      const oldIdx = items.indexOf(activeSlug);
      const newIdx = items.indexOf(overId);
      if (oldIdx >= 0 && newIdx >= 0) {
        const newGroupSlugs = arrayMove(items, oldIdx, newIdx);
        const groupSet = new Set(items);
        const positions = order.map((s, i) => (groupSet.has(s) ? i : -1)).filter((i) => i >= 0);
        const combined = [...order];
        positions.forEach((pos, i) => { if (newGroupSlugs[i]) combined[pos] = newGroupSlugs[i]!; });
        order = combined;
        setUserOrder(order);
      }
    }
    persistPrefs({ sidebar_order: order, sidebar_group_slugs: groupSlugs });
  }, [groupOfSlug, orderedAll, mainItems, groupSlugs, persistPrefs]);

  const resetPersonalization = useCallback(() => {
    setUserOrder(null);
    setMoreSlugs(new Set());
    setHiddenSlugs(new Set());
    setGroupSlugs({});
    setPinned(new Set());
    persistPrefs({ sidebar_order: null, sidebar_more_slugs: [], sidebar_hidden_slugs: [], sidebar_group_slugs: null });
  }, [persistPrefs]);

  if (!visible) return null;

  const width = collapsed ? 'w-16' : 'w-60';
  const editing = editMode && !collapsed;
  const mehrVisible = moreItems.length > 0 || editing;
  const mehrBadge = badgeInfo(moreItems);

  return (
    <aside
      className={`${width} sticky top-14 h-[calc(100vh-3.5rem)] shrink-0 border-r border-default bg-card/60 transition-[width] duration-150 overflow-y-auto overscroll-contain flex flex-col`}
      data-portal-sidebar
      data-collapsed={collapsed ? '1' : '0'}
      // Hover-Bereich = die GANZE Sidebar inkl. Scrollbalken (Joerg 2026-09-17):
      // mouseleave feuert erst, wenn der Zeiger das <aside> verlaesst.
      onMouseEnter={clearLeave}
      onMouseLeave={() => { if (isFinePointer()) scheduleLeave(); }}
    >
      {/* Header + Toggle + Bearbeiten (SP1 2026-07-21) */}
      <div className="flex items-center justify-between gap-1 px-3 py-2 border-b border-default text-xs uppercase text-muted-foreground tracking-wider">
        {!collapsed && <span>Navigation</span>}
        {!collapsed && (
          <button
            onClick={() => setEditMode((v) => !v)}
            className={`p-1 rounded ml-auto ${editMode ? 'bg-primary/20 text-primary' : 'hover:bg-primary/10 text-muted-foreground hover:text-primary'}`}
            title={editMode ? 'Bearbeiten beenden' : 'Sidebar personalisieren'}
            aria-label="Sidebar-Personalisierung"
          >
            {editMode ? <Check size={14} /> : <Settings2 size={14} />}
          </button>
        )}
        <button
          onClick={toggleCollapse}
          className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary"
          title={collapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
          aria-label="Sidebar-Toggle"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Portalweite Suche (PS-4) — vor der sortierbaren Nav, kein Teil des Edit-Modus */}
      <SidebarSearchField collapsed={collapsed} />

      {editing && (
        <div className="px-3 py-2 bg-primary/5 border-b border-default text-xs text-muted-foreground space-y-1.5">
          <div className="flex items-center gap-1 flex-wrap">Ziehen sortiert und verschiebt in andere Gruppen. <MoreHorizontal size={12} className="inline" /> = ins Mehr-Menü, <EyeOff size={12} className="inline" /> = ausblenden.</div>
          <button
            type="button"
            onClick={resetPersonalization}
            className="text-primary hover:underline"
          >
            Alle Anpassungen zurücksetzen
          </button>
        </div>
      )}

      {/* EIN DndContext ueber allen Gruppen (2026-09-18) — ausserhalb des
          Bearbeiten-Modus gibt es keine Sortables, der Kontext ist dann inert. */}
      <DndContext sensors={dndSensors} collisionDetection={closestCorners} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
      <ul className="py-1" data-sidebar-groups>
        {groups.map(({ def, items }) => {
          const { sum, title } = badgeInfo(items);
          const open = editing || pinned.has(def.slug) || hoverGroup === def.slug;
          return (
            <SidebarGroup
              key={def.slug}
              slug={def.slug}
              label={def.label}
              icon={def.icon}
              collapsed={collapsed}
              open={open}
              pinned={pinned.has(def.slug)}
              active={activeGroup === def.slug}
              editMode={editing}
              badgeSum={sum}
              badgeTitle={title}
              onHoverStart={onHoverStart}
              onHeadClick={onHeadClick}
              flyoutOpen={collapsed && flyoutGroup === def.slug}
              onFlyoutClose={closeFlyout}
              onFlyoutEnter={clearLeave}
              onFlyoutLeave={scheduleLeave}
            >
              <SortableNavSection
                groupSlug={def.slug}
                items={items}
                collapsed={false}
                location={location.pathname}
                badges={badges}
                editMode={editing}
                onMoveToMore={moveToMore}
                onHide={hideItem}
              />
            </SidebarGroup>
          );
        })}

        {/* Mehr-Menue als letzte Pseudo-Gruppe: sichtbar wenn (a) Inhalt existiert
            ODER (b) Bearbeiten-Modus aktiv (dann als Ziel + Erklaerung — sonst
            Chicken-and-Egg fuer Erstnutzer). SP1 2026-07-21 v2. */}
        {mehrVisible && (
          <SidebarGroup
            slug={MEHR_GROUP.slug}
            label={MEHR_GROUP.label}
            icon={MEHR_GROUP.icon}
            collapsed={collapsed}
            open={editing || pinned.has('mehr') || hoverGroup === 'mehr'}
            pinned={pinned.has('mehr')}
            active={activeGroup === 'mehr'}
            editMode={editing}
            badgeSum={mehrBadge.sum}
            badgeTitle={mehrBadge.title}
            countHint={moreItems.length}
            onHoverStart={onHoverStart}
            onHeadClick={onHeadClick}
            flyoutOpen={collapsed && flyoutGroup === 'mehr'}
            onFlyoutClose={closeFlyout}
            onFlyoutEnter={clearLeave}
            onFlyoutLeave={scheduleLeave}
          >
            {moreItems.length > 0 ? (
              <MoreItemsList
                items={moreItems}
                collapsed={false}
                location={location.pathname}
                badges={badges}
                editMode={editing}
                onMoveToMain={moveToMain}
                onHide={hideItem}
              />
            ) : editing ? (
              <div className="px-3 pb-3 text-xs text-muted-foreground italic">
                Klick auf <MoreHorizontal size={11} className="inline align-text-bottom" /> bei einem Sidebar-Punkt oben, um ihn ins Mehr-Menü zu verschieben. Das Auge <EyeOff size={11} className="inline align-text-bottom" /> blendet dagegen ganz aus.
              </div>
            ) : null}
          </SidebarGroup>
        )}
      </ul>
      </DndContext>

      {/* Ausgeblendet — im Bearbeiten-Modus IMMER sichtbar (auch leer), damit
          der User weiss dass es die Sektion gibt und wie er Punkte hierher
          und wieder zurueckbekommt. SP1 2026-07-21 v2. */}
      {editing && (
        <div className="border-t border-default">
          <div className="px-3 py-2 text-xs uppercase text-muted-foreground tracking-wider flex items-center gap-2">
            <EyeOff size={11} /> Ausgeblendet {hiddenItems.length > 0 && `(${hiddenItems.length})`}
          </div>
          {hiddenItems.length > 0 ? (
            <ul className="py-1">
              {hiddenItems.map((it) => {
                const Icon = it.icon;
                return (
                  <li key={it.slug} className="flex items-center gap-2 px-3 py-1.5 mx-1 my-0.5 rounded-md text-xs text-muted-foreground">
                    <Icon size={14} className={`${it.iconClassName ?? ''} flex-shrink-0 opacity-60`} />
                    <span className="truncate flex-1 opacity-70">{it.label}</span>
                    <button
                      type="button"
                      onClick={() => moveToMain(it.slug)}
                      className="p-1 rounded hover:bg-primary/10 text-primary"
                      title="Wieder einblenden"
                    >
                      <Eye size={12} />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-3 pb-3 text-xs text-muted-foreground italic">
              Klick auf <EyeOff size={11} className="inline align-text-bottom" /> bei einem Sidebar-Punkt, um ihn ganz auszublenden. Er landet dann hier und kann per <Eye size={11} className="inline align-text-bottom" /> wieder eingeblendet werden.
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

interface NavSectionProps {
  items: NavItem[];
  collapsed: boolean;
  location: string;
  badges: BadgeSnapshot;
}

function NavSection({ items, collapsed, location, badges }: NavSectionProps) {
  return (
    <ul className="py-1">
      {items.map((it) => {
        const Icon = it.icon;
        const active = isActiveHref(location, it.href);
        const badgeCount = it.badgeKey ? badges[it.badgeKey] : 0;
        return (
          <li key={it.slug}>
            <Link
              to={it.href}
              data-sidebar-item={it.slug}
              title={collapsed ? `${it.label}${badgeCount > 0 ? ` (${badgeCount})` : ''}` : undefined}
              className={`relative flex items-center gap-3 px-3 py-2 mx-1 my-0.5 rounded-md text-sm transition-colors ${
                active
                  ? 'bg-primary/15 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
              }`}
            >
              <span className="relative flex-shrink-0">
                <Icon size={16} className={it.iconClassName ?? ''} />
                <StatusDot slug={it.requiresModule} />
              </span>
              {!collapsed && <span className="truncate flex-1">{it.label}</span>}
              {badgeCount > 0 && !collapsed && (
                <span
                  title={it.badgeKey ? badges.tooltips?.[it.badgeKey] : undefined}
                  className="min-w-[18px] h-4 px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center"
                >
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
              {badgeCount > 0 && collapsed && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent" />
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

// ============================================================================
// SP1 2026-07-21: Sortable + Mehr-Menue Helper-Komponenten
// ============================================================================

interface SortableSectionProps {
  groupSlug: SidebarGroupSlug;
  items: NavItem[];
  collapsed: boolean;
  location: string;
  badges: BadgeSnapshot;
  editMode: boolean;
  onMoveToMore: (slug: string) => void;
  onHide: (slug: string) => void;
}

/** Gruppen-Inhalt. Im Bearbeiten-Modus SortableContext + Ablageflaeche der
 *  Gruppe; der DndContext liegt seit 2026-09-18 EINMAL in PortalSidebar ueber
 *  allen Gruppen, damit Eintraege auch in andere Gruppen gezogen werden koennen
 *  (vorher eigener Kontext je Gruppe, Sortieren nur innerhalb).
 *  Ausserhalb des Bearbeiten-Modus verhaelt sich die Sektion wie NavSection. */
function SortableNavSection(props: SortableSectionProps) {
  const { items, collapsed, location, badges, editMode } = props;
  if (!editMode) {
    return <NavSection items={items} collapsed={collapsed} location={location} badges={badges} />;
  }
  return <SortableGroupBody {...props} />;
}

function SortableGroupBody(props: SortableSectionProps) {
  const { groupSlug, items, collapsed, location, badges, onMoveToMore, onHide } = props;
  // Ablageflaeche der ganzen Gruppe — noetig, damit auch eine LEERE Gruppe
  // ein Ziel ist (closestCorners trifft sonst nur Eintraege).
  const { setNodeRef, isOver } = useDroppable({ id: `${DROP_PREFIX}${groupSlug}` });
  return (
    <SortableContext items={items.map((it) => it.slug)} strategy={verticalListSortingStrategy}>
      <ul
        ref={setNodeRef}
        data-sidebar-dropzone={groupSlug}
        className={`py-1 min-h-[2.5rem] rounded-md transition-colors ${isOver ? 'bg-primary/10' : ''}`}
      >
        {items.map((it) => (
          <SortableNavItem
            key={it.slug}
            item={it}
            collapsed={collapsed}
            location={location}
            badges={badges}
            editMode
            rightAction="more"
            onMoveTarget={() => onMoveToMore(it.slug)}
            onHide={() => onHide(it.slug)}
          />
        ))}
        {items.length === 0 && (
          <li className="mx-1 my-0.5 px-3 py-2 rounded-md border border-dashed border-default text-xs text-muted-foreground italic">
            Hierher ziehen
          </li>
        )}
      </ul>
    </SortableContext>
  );
}

interface MoreListProps {
  items: NavItem[];
  collapsed: boolean;
  location: string;
  badges: BadgeSnapshot;
  editMode: boolean;
  onMoveToMain: (slug: string) => void;
  onHide: (slug: string) => void;
}

/** Rendert das Mehr-Menue. Im Bearbeiten-Modus mit Zurueck-in-Sidebar + Verstecken. */
function MoreItemsList(props: MoreListProps) {
  const { items, collapsed, location, badges, editMode, onMoveToMain, onHide } = props;
  if (!editMode) {
    return (
      <div className="pb-2">
        <NavSection items={items} collapsed={collapsed} location={location} badges={badges} />
      </div>
    );
  }
  return (
    <ul className="py-1 pb-2">
      {items.map((it) => (
        <SortableNavItem
          key={it.slug}
          item={it}
          collapsed={collapsed}
          location={location}
          badges={badges}
          editMode
          rightAction="main"
          onMoveTarget={() => onMoveToMain(it.slug)}
          onHide={() => onHide(it.slug)}
          nonSortable
        />
      ))}
    </ul>
  );
}

interface SortableNavItemProps {
  item: NavItem;
  collapsed: boolean;
  location: string;
  badges: BadgeSnapshot;
  editMode: boolean;
  rightAction: 'more' | 'main';
  onMoveTarget: () => void;
  onHide: () => void;
  nonSortable?: boolean;
}

function SortableNavItem(props: SortableNavItemProps) {
  const { item, collapsed, location, badges, editMode, rightAction, onMoveTarget, onHide, nonSortable } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.slug,
    disabled: nonSortable,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const Icon = item.icon;
  const active = isActiveHref(location, item.href);
  const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;

  return (
    <li ref={setNodeRef} style={style} data-sidebar-item={item.slug}>
      <div className={`relative flex items-center gap-2 px-3 py-2 mx-1 my-0.5 rounded-md text-sm ${
        active ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground'
      }`}>
        {editMode && !collapsed && !nonSortable && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
            title="Ziehen zum Sortieren"
            aria-label="Ziehen"
          >
            <GripVertical size={14} />
          </button>
        )}
        <span className="relative flex-shrink-0">
          <Icon size={16} className={item.iconClassName ?? ''} />
          <StatusDot slug={item.requiresModule} />
        </span>
        {!collapsed && <span className="truncate flex-1">{item.label}</span>}
        {/* Pulsierender KI-Chip beim Tagebuch, wenn eine /analyze-Session laueft
            (Joerg 2026-08-11). animate-pulse ist Tailwind-Standard, blauer Farbton. */}
        {item.badgeKey === 'tagebuch' && badges.analyzing_tagebuch && !collapsed && (
          <span
            title="KI analysiert gerade dein Tagebuch"
            className="px-1.5 py-0.5 rounded-full bg-blue-500/25 text-blue-300 text-[10px] font-bold flex items-center justify-center animate-pulse"
          >
            KI
          </span>
        )}
        {badgeCount > 0 && !collapsed && (
          <span
            title={item.badgeKey ? badges.tooltips?.[item.badgeKey] : undefined}
            className="min-w-[18px] h-4 px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center"
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
        {editMode && !collapsed && (
          <>
            <button
              type="button"
              onClick={onMoveTarget}
              className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary"
              title={rightAction === 'more' ? 'Ins Mehr-Menü verschieben' : 'Zurück in Sidebar'}
            >
              {rightAction === 'more' ? <MoreHorizontal size={13} /> : <CornerLeftUp size={12} />}
            </button>
            <button
              type="button"
              onClick={onHide}
              className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-rose-400"
              title="Ausblenden"
            >
              <EyeOff size={12} />
            </button>
          </>
        )}
      </div>
    </li>
  );
}

export function useSidebarPreferences() {
  const [visible, setVisible] = useState<boolean>(() => {
    // Optimistischer Init aus LocalStorage — Server-Sync kommt via useEffect
    const cached = localStorage.getItem('onvice.sidebar.show');
    return cached === '1';
  });

  useEffect(() => {
    api.get<{ success: boolean; data: PrefsResponse }>('/portal/me/sidebar-prefs')
      .then((res) => {
        const d = res.data;
        const effective = d.sidebar_show ?? d.sidebar_show_default;
        setVisible(effective);
        localStorage.setItem('onvice.sidebar.show', effective ? '1' : '0');
      })
      .catch(() => {});
  }, []);

  return { visible, setVisible };
}
